import { styled } from '@mui/material'
import Icon from '@mui/material/Icon'
import Typography from '@mui/material/Typography'
import { isEmpty } from 'ramda'
import React from 'react'

import CustomTooltip from '@/app/component/atom/tooltip'
import FieldStatic, { DISPLAY_MAP as DEFAULT_DISPLAY_MAP } from '@/app/component/layout/field/static'
import IconText from '@/app/component/layout/icon-text'
import { TYPES } from '@/app/module/campaigns/definitions'

const getSubscriptionLockColor = (preLaunch: boolean, open: boolean) => {
  if (preLaunch) {
    return 'disabled'
  }

  return open ? 'success' : 'error'
}

const getSubscriptionLockIcon = (preLaunch: boolean, open: boolean) => (open && !preLaunch ? 'lock_open' : 'lock')

const getSubscriptionLockTooltip = ({
  draft,
  open,
  scheduled,
}: {
  draft: boolean
  open: boolean
  scheduled: boolean
}) => {
  if (draft) {
    return 'Draft campaigns are not open for subscriptions.'
  }

  if (scheduled) {
    return 'Campaigns are not open for subscriptions until they are live.'
  }

  return `${open ? 'Open' : 'Closed'} for new subscriptions. Change in Actions menu.`
}

const DISPLAY_MAP = {
  ...DEFAULT_DISPLAY_MAP,
  type: ({ field }: { field: string }) => {
    const typeConfig = TYPES[field.toUpperCase() as keyof typeof TYPES]
    if (!typeConfig) {
      return null
    }

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {typeof typeConfig.icon === 'string' && (
          <Icon color="action" fontSize="small">
            {typeConfig.icon}
          </Icon>
        )}
        {typeof typeConfig.icon === 'function' && <typeConfig.icon />}
        <Typography component="span" sx={{ marginLeft: 0.5 }}>
          {!isEmpty(field) ? typeConfig.title : ''}
        </Typography>
      </div>
    )
  },
  state: ({
    field,
  }: {
    field: {
      aborted?: boolean
      archived?: boolean
      launched: boolean
      open: boolean
      published: boolean
    }
  }) => {
    const { aborted, archived, published, launched } = field

    const archivedText = archived ? ' (archived)' : null

    if (aborted) {
      return (
        <IconText>
          <Icon>block</Icon>
          <Typography>stopped {archivedText}</Typography>
        </IconText>
      )
    }
    if (published !== launched) {
      return (
        <IconText>
          <Icon>schedule</Icon>
          <Typography>registering {archivedText}</Typography>
        </IconText>
      )
    }
    if (published && launched) {
      return (
        <IconText>
          <Icon>sensors</Icon>
          <Typography>live {archivedText}</Typography>
        </IconText>
      )
    }
    return (
      <IconText>
        <Icon>create</Icon>
        <Typography>draft {archivedText}</Typography>
      </IconText>
    )
  },
  runs: ({
    field: {
      started,
      launched,
      open,
      published,
      inProgress,
      live,
      scheduled,
      completed,
      endpointReached,
      unsubscribed,
      error,
    },
  }: {
    field: {
      started: number
      launched: boolean
      open: boolean
      published: boolean
      inProgress: number
      live: number
      scheduled: number
      completed: number
      endpointReached: number
      unsubscribed: number
      error: number
    }
  }) => {
    const draft = !published && !launched
    const scheduledCampaign = published && !launched
    const preLaunch = draft || scheduledCampaign
    const showSubscriptionLock = preLaunch || typeof open === 'boolean'

    return (
      <IconText>
        {showSubscriptionLock && (
          <CustomTooltip title={getSubscriptionLockTooltip({ draft, open, scheduled: scheduledCampaign })}>
            <Icon color={getSubscriptionLockColor(preLaunch, open)}>{getSubscriptionLockIcon(preLaunch, open)}</Icon>
          </CustomTooltip>
        )}
        <Typography className="runs-stats">
          {started}
          <SubStatsWrapper>
            <CustomTooltip
              title={
                <Typography>
                  <span>{live} live</span>
                  <br />
                  <span>{scheduled} scheduled</span>
                </Typography>
              }
            >
              <span className="runs-in-progress">({inProgress} in progress</span>
            </CustomTooltip>
            ,{' '}
            <CustomTooltip
              title={
                <Typography>
                  <span>{endpointReached} completed</span>
                  <br />
                  <span>{unsubscribed} unsubscribed</span>
                  <br />
                  <span>{error} failed</span>
                </Typography>
              }
            >
              <span className="runs-done">{completed} done)</span>
            </CustomTooltip>
          </SubStatsWrapper>
        </Typography>
      </IconText>
    )
  },
}

type Props = {
  field: any
  name: string
  specs: any
}

const Field: React.FC<Props> = ({ field, name, specs }) => (
  <FieldStatic
    displayMap={DISPLAY_MAP}
    field={typeof field === 'string' && field.length > 100 ? `${field.substring(0, 98)}[...]` : field}
    name={name}
    specs={specs}
    title={typeof field === 'string' ? field : ''}
  />
)

const SubStatsWrapper = styled('span')(() => ({
  marginLeft: '0.35rem',
  opacity: '0.45',
  borderBottom: '2.5px dashed grey',
}))

export default Field
